import { IPROP_MAP, modifyProps } from '../../generators/modifyProps';

import { JDSTabsDesktopProps } from './generated';
import { ReactElement } from 'react';
import { useRootRef } from '../../hooks/useRootRef';
import { useSelected } from '../../hooks/useSelected';

export interface TabItemProps {
  label: string;
  icon?: string;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  children?: React.ReactNode;
}

const legacyOverflows = ['arrow'] as const;
type legacyOverflowsType = (typeof legacyOverflows)[number];

const legacyTabAppearance = ['tab-bar'] as const;
type legacyTabAppearanceType = (typeof legacyTabAppearance)[number];

export interface TabsProps
  extends Omit<JDSTabsDesktopProps, 'appearance' | 'overflow' | 'onChange'> {
  overflow?: legacyOverflowsType | JDSTabsDesktopProps['overflow'];
  appearance?: JDSTabsDesktopProps['appearance'] | legacyTabAppearanceType;
  children?: ReactElement<TabItemProps>[];
  activeTab?: number;
  onChange?: (index: number) => void;
  maxWidth?: boolean;
}

const getOverflow = (props: TabsProps) => {
  if (['tab-bar', 'tab_bar'].includes(props.appearance as string)) {
    if (['arrow', 'arrows'].includes(props.overflow as string)) {
      return 'scroll';
    }
  }
  return props.overflow;
};

export const PROP_MAP: IPROP_MAP<TabsProps> = (props) => {
  if (props.appearance === 'tab-bar') {
    console.warn('appearance tab-bar is deprecated. Use tab_bar instead');
  }
  if (props.overflow === 'arrow') {
    console.warn('overflow arrrow is deprecated. Use arrow instead');
  }
  return {
    appearance: {
      variations: {
        normal: {
          appearance: 'normal'
        },
        'tab-bar': {
          appearance: 'tab_bar'
        }
      }
    },
    overflow: {
      variations: {
        arrow: {
          overflow: 'arrows'
        }
      },
      normal: {
        overflow: 'normal'
      }
    }
  };
};

const TAB_LIST_CLASS = 'div#tablist-container';
const SCROLLABLE_CLASS = '.j-JDSTabBar-tab-container';

export const withLogic =
  (Component: React.ComponentType<TabsProps>): React.FC<TabsProps> =>
  (userProps: TabsProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const { element, rootRef } = useRootRef();

    const { index, onClick } = useSelected(props.activeTab || 0);

    const items = props.items || props.children?.map((c) => c.props);

    const tabListElement = element?.querySelector(TAB_LIST_CLASS);
    const scrollableELement = element?.querySelector(SCROLLABLE_CLASS);

    const hasIcon = items?.every((i) => i?.icon);
    return (
      <Component
        {...{
          ...props,
          items,
          rootRef,
          _kind: hasIcon ? 'label_icon' : 'label',
          activeTab: index,
          overflow: getOverflow(props),
          onNextClick: () => {
            if (tabListElement && scrollableELement) {
              const scrollValue = Math.min(
                tabListElement.scrollWidth,
                tabListElement.scrollLeft + tabListElement.clientWidth
              );

              tabListElement.scrollTo({
                left: scrollValue,
                behavior: 'smooth'
              });
            }
          },
          onPrevClick: () => {
            if (tabListElement) {
              const scrollValue = Math.max(
                0,
                tabListElement.scrollLeft - tabListElement.clientWidth
              );

              tabListElement.scrollTo({
                left: scrollValue,
                behavior: 'smooth'
              });
            }
          },
          content: items?.[index]?.children,
          onChange: (i: number) => {
            onClick(i);
            props.onChange?.(i);
          }
        }}
      />
    );
  };
